import {
  Form,
  Input,
  Popconfirm,
  Table,
  Typography,
  Select,
  message,
  Button,
  Radio,
  Modal,
  Space,
  Layout,
  DatePicker,
  Upload,
  Alert,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import "./index.css";
import httpUtil from "../../../../utils/httpUtil";
import dayjs from "dayjs";

const { Option } = Select;
const { Header } = Layout;

export const StudentsMaster = () => {
  useEffect(() => {
    getAllStudents();
  }, []);

  // 外层表格的 Form 实例（用于编辑行数据）
  const [tableForm] = Form.useForm();
  // 独立的 Modal Form 实例（用于添加学生）
  const [modalForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [addStudentRadioValue, setAddStudentRadioValue] = useState("single");
  const [searchParams, setSearchParams] = useState({
    student_id: "",
    name: "",
    college: "",
    major: "",
    class: "",
    enrollment_date: "",
  });
  //excel添加
  const [fileList, setFileList] = useState([]);
  const [errors, setErrors] = useState([]); // 存储导入错误信息

  // 表格编辑逻辑
  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    children,
    ...restProps
  }) => {
    const getInputNode = () => {
      if (dataIndex === "sex") {
        return (
          <Radio.Group>
            <Radio value="男">男</Radio>
            <Radio value="女">女</Radio>
          </Radio.Group>
        );
      } else if (dataIndex === "enrollment_date") {
        return (
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            allowClear={false}
            // value 是由 Form.Item 控制的，不需要你传 value
          />
        );
      }
      return <Input />;
    };

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `请输入${title}` }]}
          >
            {getInputNode()}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const isEditing = (record) => record.student_id === editingKey;
  const edit = (record) => {
    tableForm.setFieldsValue({
      ...record,
      enrollment_date: record.enrollment_date
        ? dayjs(record.enrollment_date, "YYYY-MM-DD")
        : null,
    });
    setEditingKey(record.student_id);
  };
  const cancel = () => setEditingKey("");

  const save = async (key) => {
    try {
      const row = await tableForm.validateFields();

      if (
        row.enrollment_date &&
        typeof row.enrollment_date.format === "function"
      ) {
        row.enrollment_date = row.enrollment_date.format("YYYY-MM-DD");
      }

      const newData = [...data];
      const index = newData.findIndex((item) => item.student_id === key);
      if (index > -1) {
        const updatedUser = { ...newData[index], ...row };
        httpUtil
          .updateStudents(updatedUser)
          .then((res) => {
            if (res.success) {
              newData.splice(index, 1, updatedUser);
              setData(newData);
              setEditingKey("");
              message.success("更新成功");
            } else {
              message.error(res.error || "更新失败");
            }
          })
          .catch((err) => {
            console.error("更新出错：", err);
            message.error("更新请求失败");
          });
      }
    } catch (err) {
      console.log("验证失败:", err);
    }
  };

  // 其他操作（删除、重置密码等）
  const handleDelete = (key) => {
    const params = {
      student_id: key,
    };
    try {
      httpUtil.deleteStudents(params).then((res) => {
        if (res.success) {
          const newData = data.filter((item) => item.student_id !== key);
          setData(newData);
          message.success(`用户 ${key} 删除成功`);
        } else {
          message.error(res.error || "删除用户失败");
        }
      });
    } catch (err) {
      console.error("删除用户错误:", err);
      message.error("删除用户时发生错误");
    }
  };

  // Modal 表单逻辑
  const showModal = () => setOpen(true);

  const handleCancel = () => {
    modalForm.resetFields();
    setErrors([])
    setOpen(false);
  };

  const handleOk = () => {
    switch (addStudentRadioValue) {
      case "single":
        modalForm.submit();
        break;
      case "excel":
        handleExcelUpload();
        break;

      default:
        break;
    }
  };

  const handleAddStudent = (values) => {
    setConfirmLoading(true);
    const data = {
      student_id: values.student_id,
      name: values.name,
      sex: values.sex,
      college: values.college,
      major: values.major,
      class: values.class,
      enrollment_date: values.enrollment_date.format("YYYY-MM-DD"),
    };
    httpUtil
      .addStudentSingle(data)
      .then((res) => {
        if (res.success) {
          message.success(res.message);
          modalForm.resetFields();
        } else {
          message.error(res.error); // 处理业务逻辑错误
        }
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  // 表格列配置
  const columns = [
    {
      title: "学号",
      dataIndex: "student_id",
      width: 150, // 固定宽度
      editable: false,
      fixed: "left",
    },
    {
      title: "姓名",
      dataIndex: "name",
      width: 100,
      editable: true,
    },
    {
      title: "性别",
      dataIndex: "sex",
      width: 80,
      editable: true,
      render: (text) => text, // 非编辑状态下只显示文字
    },
    {
      title: "学院",
      dataIndex: "college",
      width: 180,
      editable: true,
    },
    {
      title: "专业",
      dataIndex: "major",
      width: 180,
      editable: true,
    },
    {
      title: "班级",
      dataIndex: "class",
      width: 120,
      editable: true,
    },
    {
      title: "入学日期",
      dataIndex: "enrollment_date",
      width: 120,
      editable: true,
      render: (text) => text,
    },
    {
      title: "操作",
      dataIndex: "operation",
      width: 200,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Typography.Link onClick={() => save(record.student_id)}>
              保存
            </Typography.Link>
            <Typography.Link onClick={cancel}>取消</Typography.Link>
          </Space>
        ) : (
          <Space>
            <Typography.Link
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
            >
              编辑
            </Typography.Link>
            <Popconfirm
              title="确定删除此学生吗?"
              onConfirm={() => handleDelete(record.student_id)}
              okText="确定"
              cancelText="取消"
            >
              <Typography.Link type="danger">删除</Typography.Link>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  //获取所有学生
  const getAllStudents = () => {
    setLoading(true);
    setPagination({ current: 1, pageSize: 10 });
    httpUtil
      .getAllStudents()
      .then((res) => {
        if (res.success) {
          setData(res.data);
        } else {
          message.error(res.error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  //搜索学生
  const handleSearch = () => {
    setLoading(true);
    const {
      student_id,
      name,
      college,
      major,
      class: className,
      enrollment_date,
    } = searchParams;
    const query = {
      student_id,
      name,
      college,
      major,
      class: className,
      enrollment_date,
    };
    httpUtil
      .searchStudents(query)
      .then((res) => {
        console.log(res);
        if (res.success) {
          message.success("搜索成功！");
          const reset = {
            student_id: "",
            name: "",
            college: "",
            major: "",
            class: "",
            enrollment_date: "",
          };
          setSearchParams(reset);
          setData(res.data);
          setPagination({ current: 1, pageSize: 10 });
        } else {
          message.error(res.error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //重置
  const handleReset = () => {
    const reset = {
      student_id: "",
      name: "",
      college: "",
      major: "",
      class: "",
      enrollment_date: "",
    };
    setSearchParams(reset);
    setPagination({ current: 1, pageSize: 10 });
    getAllStudents(); // 清空条件，重新获取所有学生
  };

  // 添加 Excel 上传处理函数
  const handleExcelUpload = async () => {
    if (fileList.length === 0) {
      message.warning("请先选择文件");
      return;
    }

    setConfirmLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", fileList[0].originFileObj);

      const response = await httpUtil.uploadStudentsExcel(
        formData,
        (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
        }
      );

      if (response.success) {
        message.success(`导入成功 ${response.data.successCount} 条记录`);
        if (response.data.errorCount > 0) {
          message.warning(`${response.data.errorCount} 条记录导入失败`);
          setErrors(response.data.errors || []);
        }
        setFileList([]);
        getAllStudents(); 
      } else {
        message.error(response.error || "导入失败");
      }
    } catch (error) {
      console.error("上传失败:", error);
      message.error("上传失败: " + (error.message || "未知错误"));
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Form form={tableForm} component={false}>
      <Header style={{ padding: 0, background: "#fff" }}>
        <Space align="center">
          {/* 学号 */}
          <Input
            placeholder="请输入学号"
            value={searchParams.student_id}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                student_id: e.target.value,
              }))
            }
            style={{ width: 100 }}
          />
          {/* 姓名 */}
          <Input
            placeholder="请输入姓名"
            value={searchParams.name}
            onChange={(e) =>
              setSearchParams((prev) => ({ ...prev, name: e.target.value }))
            }
            style={{ width: 100 }}
          />
          {/* 学院 */}
          <Input
            placeholder="请输入学院"
            value={searchParams.college}
            onChange={(e) =>
              setSearchParams((prev) => ({ ...prev, college: e.target.value }))
            }
            style={{ width: 100 }}
          />
          {/* 专业 */}
          <Input
            placeholder="请输入专业"
            value={searchParams.major}
            onChange={(e) =>
              setSearchParams((prev) => ({ ...prev, major: e.target.value }))
            }
            style={{ width: 100 }}
          />
          {/* 班级 */}
          <Input
            placeholder="请输入班级"
            value={searchParams.class}
            onChange={(e) =>
              setSearchParams((prev) => ({ ...prev, class: e.target.value }))
            }
            style={{ width: 100 }}
          />
          {/* 注册日期 */}
          <Input
            placeholder="请输入注册日期"
            value={searchParams.enrollment_date}
            onChange={(e) =>
              setSearchParams((prev) => ({
                ...prev,
                enrollment_date: e.target.value,
              }))
            }
            style={{ width: 150 }}
          />
          <Button type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
          <Button type="primary" onClick={showModal}>
            添加学生信息
          </Button>
        </Space>
      </Header>

      <Table
        loading={loading}
        components={{ body: { cell: EditableCell } }}
        bordered
        dataSource={data}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            cancel();
          },
        }}
        scroll={{ y: 400, x: "max-content" }}
      />

      {/* 添加学生的 Modal */}
      <Modal
        title="添加学生信息"
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        destroyOnClose
        okText= {confirmLoading ? "正在导入..." : "确定导入"}
        // loading={uploading}
        cancelText={"取消"}
      >
        <Radio.Group
          value={addStudentRadioValue}
          onChange={(e) => setAddStudentRadioValue(e.target.value)}
        >
          <Radio value="single">单个学生添加</Radio>
          <Radio value="excel">学生批量Excel导入</Radio>
        </Radio.Group>
        <div className="addusercontent">
          {addStudentRadioValue === "single" ? (
            <Form
              form={modalForm}
              name="addStudent"
              labelCol={{ span: 5, offset: 3 }}
              wrapperCol={{ span: 12 }}
              labelAlign="left"
              onFinish={handleAddStudent}
              autoComplete="off"
            >
              <Form.Item
                label={"学\u3000\u3000号"}
                name="student_id"
                rules={[{ required: true, message: "请输入学号!" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={"姓\u3000\u3000名"}
                name="name"
                rules={[{ required: true, message: "请输入姓名!" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={"性\u3000\u3000别"}
                name="sex"
                rules={[{ required: true, message: "请选择性别!" }]}
              >
                <Select>
                  <Option value="男">男</Option>
                  <Option value="女">女</Option>
                </Select>
              </Form.Item>
              <Form.Item
                label={"学\u3000\u3000院"}
                name="college"
                rules={[{ required: true, message: "请输入学院!" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={"专\u3000\u3000业"}
                name="major"
                rules={[{ required: true, message: "请输入专业!" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={"班\u3000\u3000级"}
                name="class"
                rules={[{ required: true, message: "请输入班级!" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="入学日期"
                name="enrollment_date"
                rules={[{ required: true, message: "请选择入学日期!" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={"请选择入学日期"}
                />
              </Form.Item>
            </Form>
          ) : (
            <div style={{ padding: "20px 0" }}>
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={(file) => {
                  const isExcel = [
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel",
                  ].includes(file.type);

                  if (!isExcel) {
                    message.error("仅支持 .xlsx 或 .xls 格式的文件");
                    return Upload.LIST_IGNORE;
                  }

                  const MAX_SIZE = 5 * 1024 * 1024;
                  if (file.size > MAX_SIZE) {
                    message.error("文件大小不能超过5MB");
                    return Upload.LIST_IGNORE;
                  }

                  // 使用回调方式确保获取最新状态
                  setFileList([
                    { ...file, status: "done",name: file.name, originFileObj: file },
                  ]);
                  return false;
                }}
                fileList={fileList}
                maxCount={1}
                onRemove={() => setFileList([])}
              >
                <Button icon={<UploadOutlined />}>选择Excel文件</Button>
              </Upload>

              {/* 错误信息显示区域 */}
              {errors.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Alert
                    message={`${errors.length} 条记录导入失败`}
                    type="error"
                    showIcon
                    description={
                      <div style={{ maxHeight: 200, overflow: "auto" }}>
                        {errors.map((err, i) => (
                          <p key={i}>
                            <strong>第 {err.row} 行:</strong> {err.error}
                            {err.data && (
                              <span style={{ color: "#888", marginLeft: 8 }}>
                                (学号: {err.data.学号 || "无"})
                              </span>
                            )}
                          </p>
                        ))}
                      </div>
                    }
                  />
                </div>
              )}

              {/* <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={handleExcelUpload}
                  loading={uploading}
                  disabled={fileList.length === 0}
                >
                  {uploading ? "正在导入..." : "开始导入"}
                </Button>
              </div> */}
              <div style={{ marginTop: 16, color: "#666" }}>
                <p>Excel模板要求：</p>
                <ul>
                  <li>
                    第一行必须包含标题：学号、姓名、性别、学院、专业、班级、入学日期
                  </li>
                  <li>日期格式为YYYY-MM-DD</li>
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        httpUtil.downloadExcelTemplate().catch(() => {
                          message.error("模板下载失败");
                        });
                      }}
                    >
                      模板下载
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </Form>
  );
};
